import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../utils/api';

function Header() {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const roles = user?.roles || [];
  const isManager = roles.includes('manager') && !roles.includes('pos');
  const isSuperAdmin = roles.includes('super_admin');
  const showManagerArea = isManager || isSuperAdmin; // super admin sees the management tabs too
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(
    localStorage.getItem('selectedLocation') || ''
  );

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      const locs = response.data.locations;
      setLocations(locs);
      if (locs.length > 0) {
        const saved = localStorage.getItem('selectedLocation');
        const validSaved = saved && locs.some(l => l.id.toString() === saved);
        const defaultId = validSaved ? saved : locs[0].id.toString();
        setSelectedLocation(defaultId);
        localStorage.setItem('selectedLocation', defaultId);
        // Always dispatch so POS.js gets the validated location
        window.dispatchEvent(new CustomEvent('locationchange', { detail: defaultId }));
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleLocationChange = (e) => {
    const locationId = e.target.value;
    setSelectedLocation(locationId);
    localStorage.setItem('selectedLocation', locationId);
    window.dispatchEvent(new CustomEvent('locationchange', { detail: locationId }));
  };

  useEffect(() => {
    const onRevert = (e) => {
      if (e.detail) setSelectedLocation(e.detail);
    };
    window.addEventListener('locationrevert', onRevert);
    return () => window.removeEventListener('locationrevert', onRevert);
  }, []);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  return (
    <header style={{
      background: 'white',
      borderBottom: '1px solid #ece0d1',
      boxShadow: '0 1px 4px rgba(61, 43, 31, 0.06)',
      position: 'sticky',
      top: 0,
      zIndex: 100
    }}>
      <div style={{
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: '1600px',
        margin: '0 auto'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg viewBox="0 0 995 329" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ height: '28px', width: 'auto' }}>
              <path d="M483.625 62.6664C487.601 48.751 489.589 32.8477 485.613 19.9262C483.625 12.9685 479.649 7.00479 473.685 4.02292C465.733 -0.946869 455.793 -0.946869 447.841 2.035C437.901 6.01084 428.955 13.9625 421.003 20.9202C415.039 26.8839 409.075 33.8416 404.105 40.7994C405.099 33.8416 407.087 27.8779 407.087 20.9202C407.087 16.9444 408.081 12.9685 407.087 8.99271C406.093 5.01688 404.105 4.02292 400.129 3.02896C395.159 2.035 389.195 2.035 384.225 3.02896C378.261 2.035 374.285 4.02292 372.297 8.99271C370.309 13.9625 370.309 18.9323 370.309 23.9021C370.309 27.8779 369.315 31.8537 369.315 36.8235C367.327 58.6906 368.321 80.5577 367.327 102.425V108.388C367.327 110.376 367.327 111.37 366.333 113.358C363.351 118.328 360.369 123.298 356.393 127.274C349.435 137.213 340.489 146.159 330.549 152.123C324.585 156.098 316.633 160.074 308.681 160.074C299.735 159.08 295.759 151.129 293.771 143.177C292.777 140.195 292.777 137.213 292.777 135.225C317.627 122.304 335.519 96.461 340.489 68.6302C343.471 52.7268 344.465 33.8416 333.531 19.9262C325.579 9.98666 313.651 6.01084 301.723 7.99875C295.759 8.99271 289.795 11.9746 284.825 14.9565C279.855 18.9323 275.879 22.9081 272.897 28.8719C263.951 43.7812 261.963 61.6725 260.969 79.5637C259.975 96.461 259.975 113.358 260.969 130.256C260.969 134.231 261.963 138.207 262.957 142.183C262.957 144.171 263.951 146.159 263.951 147.153C263.951 148.147 263.951 149.141 264.945 150.135C264.945 151.129 264.945 152.123 263.951 153.117C260.969 161.068 256.993 168.026 251.029 174.984C244.071 183.929 232.143 195.857 220.215 192.875C209.281 189.893 201.329 171.008 199.341 159.08C224.191 140.195 237.113 106.401 237.113 75.5879C237.113 64.6543 235.125 52.7268 230.155 42.7873C223.197 27.8779 207.293 20.9202 189.401 27.8779C175.485 33.8417 170.515 46.7631 166.539 60.6785C162.563 74.5939 160.575 99.4429 161.569 113.358C162.563 133.237 165.545 156.098 171.509 174.984C176.479 190.887 181.449 202.814 195.365 212.754C200.335 216.73 206.299 219.712 213.257 220.706C220.215 221.7 227.173 220.706 234.131 219.712C249.041 215.736 261.963 199.833 270.909 187.905C272.897 184.923 274.885 181.941 276.873 177.966C279.855 182.935 284.825 186.911 289.795 189.893C296.753 193.869 303.711 194.863 311.663 193.869C329.555 191.881 344.465 182.935 356.393 168.026C359.375 164.05 362.357 160.074 365.339 155.104C366.333 154.111 368.321 151.129 368.321 150.135C371.303 186.911 372.297 200.827 385.219 248.537C387.207 255.494 400.129 258.476 405.099 253.506C412.057 246.549 408.081 237.603 407.087 229.651C402.117 195.857 399.135 162.062 399.135 128.268C419.015 134.231 439.889 122.304 453.805 109.382C468.715 96.461 478.655 80.5577 483.625 62.6664ZM194.371 131.25C192.383 118.328 190.395 97.4549 192.383 77.5758C194.371 59.6846 199.341 48.751 202.323 47.7571C206.299 47.7571 207.293 53.7208 207.293 56.7027C209.281 62.6664 210.275 68.6302 210.275 74.5939C210.275 88.5093 207.293 107.395 201.329 120.316C199.341 124.292 197.353 127.274 194.371 131.25ZM293.771 59.6846C295.759 50.7389 300.729 31.8537 309.675 31.8537C316.633 31.8537 315.639 47.7571 315.639 50.7389C314.645 63.6604 312.657 76.5818 306.693 88.5093C303.711 95.467 298.741 101.431 291.783 105.407C290.789 91.4912 290.789 73.6 293.771 59.6846ZM449.829 62.6664C445.853 70.6181 439.889 80.5577 430.943 88.5093C414.045 102.425 406.093 98.4489 405.099 97.4549C404.105 96.461 405.099 93.4791 405.099 92.4852C408.081 76.5818 418.021 61.6725 428.955 48.751C431.937 44.7752 446.847 27.8779 454.799 32.8477C460.763 35.8296 454.799 52.7268 449.829 62.6664ZM58.1929 165.044C54.2169 161.068 50.2409 156.098 47.2589 152.123C39.3069 143.177 32.3489 132.243 30.3609 120.316C28.3729 109.382 32.3489 99.4429 41.2949 92.4852C48.2529 86.5214 55.2109 80.5577 50.2409 73.6C46.2649 68.6302 39.3069 67.6362 26.3849 73.6C15.4509 78.5697 -3.43511 93.4791 0.540893 130.256C3.52289 154.111 20.4209 174.984 36.3249 190.887C58.1929 213.748 67.1389 236.609 66.1449 255.494C65.1509 266.428 60.1809 275.373 53.2229 284.319C50.2409 288.295 46.2649 292.271 42.2889 296.246C39.3069 299.228 35.3309 302.21 32.3489 306.186C27.3789 313.144 29.3669 323.083 37.3189 327.059C44.2769 330.041 52.2289 329.047 59.1869 325.071C76.0849 316.126 87.0189 302.21 93.9769 283.325C100.935 264.44 100.935 243.567 94.9709 224.682C89.0069 201.821 74.0969 182.935 58.1929 165.044Z" fill="#c88346"></path>
              <path d="M235.125 250.524C234.131 249.531 232.143 249.531 231.149 249.531C218.227 247.543 207.293 249.531 195.365 251.518C192.383 252.512 161.569 259.47 160.575 258.476C151.629 214.742 143.677 173.99 140.695 158.086C137.713 144.171 135.725 130.256 132.743 116.34C130.755 103.419 127.773 90.4973 124.791 77.5758C122.803 69.6241 119.821 61.6725 114.851 55.7087C112.863 52.7269 108.887 50.7389 105.905 51.7329C102.923 51.7329 96.9589 55.7087 96.9589 58.6906C96.9589 64.6544 97.9529 70.6181 98.9469 76.5819L101.929 101.431C104.911 121.31 106.899 141.189 109.881 161.068L118.827 223.688C120.815 239.591 123.797 255.494 126.779 271.398C127.773 277.361 128.767 284.319 131.749 290.283C132.743 293.265 134.731 296.247 138.707 297.24C142.683 298.234 169.521 291.277 179.461 289.289C189.401 287.301 229.161 282.331 236.119 278.355C242.083 275.373 243.077 269.41 243.077 262.452C241.089 255.494 239.101 252.512 235.125 250.524ZM607.875 15.9504C601.911 8.99272 588.989 9.98668 583.025 24.896C569.109 60.6785 554.199 83.5396 538.295 94.4731C537.301 95.4671 534.319 98.4489 533.325 94.4731V88.5093C533.325 84.5335 534.319 81.5516 534.319 77.5758C536.307 65.6483 537.301 54.7148 537.301 43.7812C537.301 33.8417 538.295 23.9021 536.307 13.9625C535.313 7.99876 531.337 0.0471012 524.379 3.02897C516.427 6.01085 514.439 17.9383 512.451 25.89C509.469 35.8296 508.475 45.7692 506.487 55.7087C505.493 65.6483 504.499 76.5819 504.499 86.5214C504.499 96.461 503.505 106.401 506.487 115.346C507.481 118.328 509.469 121.31 511.457 123.298C513.445 125.286 516.427 127.274 519.409 128.268C525.373 130.256 533.325 130.256 539.289 128.268C554.199 124.292 565.133 110.376 574.079 99.4429L575.073 98.4489C556.187 178.96 530.343 226.669 505.493 221.7C497.541 219.712 492.571 210.766 491.577 203.808C489.589 194.863 488.595 185.917 485.613 176.972C483.625 171.008 477.661 160.074 466.727 165.044C456.787 169.02 457.781 182.935 458.775 195.857C459.769 211.76 468.715 251.518 503.505 254.5C539.289 257.482 568.115 235.615 588.989 158.086C597.935 126.28 610.857 63.6604 611.851 29.8658C611.851 26.884 611.851 19.9263 607.875 15.9504ZM802.699 41.7933C789.777 40.7994 781.825 52.7269 776.855 63.6604C748.029 132.243 714.233 195.857 692.365 265.434C690.377 272.392 693.359 281.337 700.317 283.325C712.245 288.295 719.203 275.373 722.185 267.422C733.119 238.597 744.053 210.766 755.981 183.929C765.921 186.911 774.867 188.899 784.807 191.881C780.831 223.688 776.855 248.537 773.873 289.289C772.879 299.228 782.819 310.162 793.753 304.198C800.711 300.222 801.705 289.289 802.699 281.337C812.639 208.778 820.591 138.207 822.579 64.6544C823.573 53.7208 815.621 42.7873 802.699 41.7933ZM795.741 88.5093C795.741 87.5154 795.741 87.5154 795.741 88.5093H796.735V89.5033C792.759 114.352 789.777 139.201 786.795 164.05L766.915 158.086C776.855 135.225 785.801 112.364 795.741 88.5093ZM985.595 106.401C961.739 95.4671 942.853 87.5154 918.003 78.5698C906.075 74.5939 894.147 70.6181 882.219 67.6362C873.273 65.6483 860.351 61.6725 854.387 69.6241C846.435 78.5698 856.375 90.4973 865.321 94.4731C876.255 98.4489 886.195 101.431 895.141 104.413C888.183 122.304 883.213 140.195 878.243 158.086C862.339 211.76 842.459 251.518 828.543 305.192C825.561 319.108 834.507 325.071 840.471 324.077C850.411 322.089 856.375 306.186 863.333 287.301C885.201 226.669 909.057 174.984 923.967 132.243C925.955 126.28 927.943 121.31 929.931 115.346C941.859 119.322 954.781 123.298 968.697 128.268C993.547 138.207 1003.49 114.352 985.595 106.401ZM681.431 225.676C677.455 223.688 672.485 222.694 667.515 224.682C663.539 225.676 659.563 227.663 655.587 227.663C650.617 227.663 645.647 224.682 644.653 218.718C642.665 212.754 643.659 206.79 643.659 200.827C644.653 174.984 649.623 149.141 656.581 124.292C659.563 112.364 663.539 100.437 667.515 89.5033C669.503 83.5396 672.485 77.5758 675.467 71.6121C678.449 66.6423 681.431 59.6846 687.395 56.7027C693.359 53.7208 701.311 56.7027 705.287 64.6544C708.269 70.6181 708.269 76.5819 713.239 81.5516C718.209 87.5154 730.137 89.5033 737.095 78.5698C743.059 68.6302 737.095 54.7148 731.131 45.7692C726.161 37.8175 719.203 29.8658 709.263 25.89C699.323 21.9142 686.401 23.9021 676.461 27.8779C666.521 31.8538 657.575 38.8115 651.611 48.751C643.659 59.6846 638.689 70.6181 633.719 82.5456C628.749 94.4731 624.773 106.401 620.797 118.328C616.821 130.256 613.839 143.177 611.851 156.098C609.863 170.014 608.869 183.929 609.863 197.845C610.857 210.766 612.845 222.694 618.809 234.621C623.779 244.561 632.725 251.518 643.659 255.494C652.605 258.476 664.533 259.47 673.479 255.494C680.437 252.512 690.377 244.561 689.383 235.615C688.389 230.645 685.407 227.663 681.431 225.676Z" fill="#c88346"></path>
            </svg>
          </div>
          
          <nav style={{ display: 'flex', gap: '4px' }}>
            {!showManagerArea && (
              <Link
                to="/"
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  color: location.pathname === '/' ? '#c88346' : '#7a6b5d',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  background: location.pathname === '/' ? '#fdf3e8' : 'transparent',
                  transition: 'all 0.2s ease',
                  letterSpacing: '-0.01em'
                }}
              >
                Point of Sale
              </Link>
            )}
            <Link
              to="/orders"
              style={{
                fontSize: '13px',
                fontWeight: '600',
                textDecoration: 'none',
                color: location.pathname === '/orders' ? '#c88346' : '#7a6b5d',
                padding: '6px 14px',
                borderRadius: '8px',
                background: location.pathname === '/orders' ? '#fdf3e8' : 'transparent',
                transition: 'all 0.2s ease',
                letterSpacing: '-0.01em'
              }}
            >
              Orders
            </Link>
            {showManagerArea && (
              <Link
                to="/reports"
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  color: location.pathname === '/reports' ? '#c88346' : '#7a6b5d',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  background: location.pathname === '/reports' ? '#fdf3e8' : 'transparent',
                  transition: 'all 0.2s ease',
                  letterSpacing: '-0.01em'
                }}
              >
                Reports
              </Link>
            )}
            {showManagerArea && (
              <Link
                to="/discounts"
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  color: location.pathname === '/discounts' ? '#c88346' : '#7a6b5d',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  background: location.pathname === '/discounts' ? '#fdf3e8' : 'transparent',
                  transition: 'all 0.2s ease',
                  letterSpacing: '-0.01em'
                }}
              >
                Discounts
              </Link>
            )}
            {showManagerArea && (
              <Link
                to="/manage-products"
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  color: location.pathname === '/manage-products' ? '#c88346' : '#7a6b5d',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  background: location.pathname === '/manage-products' ? '#fdf3e8' : 'transparent',
                  transition: 'all 0.2s ease',
                  letterSpacing: '-0.01em'
                }}
              >
                Products
              </Link>
            )}
            {isSuperAdmin && (
              <Link
                to="/access-control"
                style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  color: location.pathname === '/access-control' ? '#c88346' : '#7a6b5d',
                  padding: '6px 14px',
                  borderRadius: '8px',
                  background: location.pathname === '/access-control' ? '#fdf3e8' : 'transparent',
                  transition: 'all 0.2s ease',
                  letterSpacing: '-0.01em'
                }}
              >
                Access
              </Link>
            )}
            <Link
              to="/eod-report"
              style={{
                fontSize: '13px',
                fontWeight: '600',
                textDecoration: 'none',
                color: location.pathname === '/eod-report' ? '#c88346' : '#7a6b5d',
                padding: '6px 14px',
                borderRadius: '8px',
                background: location.pathname === '/eod-report' ? '#fdf3e8' : 'transparent',
                transition: 'all 0.2s ease',
                letterSpacing: '-0.01em'
              }}
            >
              EOD Report
            </Link>
            <Link
              to="/inventory"
              style={{
                fontSize: '13px',
                fontWeight: '600',
                textDecoration: 'none',
                color: location.pathname === '/inventory' ? '#c88346' : '#7a6b5d',
                padding: '6px 14px',
                borderRadius: '8px',
                background: location.pathname === '/inventory' ? '#fdf3e8' : 'transparent',
                transition: 'all 0.2s ease',
                letterSpacing: '-0.01em'
              }}
            >
              Inventory
            </Link>
            <Link
              to="/activity-log"
              style={{
                fontSize: '13px',
                fontWeight: '600',
                textDecoration: 'none',
                color: location.pathname === '/activity-log' ? '#c88346' : '#7a6b5d',
                padding: '6px 14px',
                borderRadius: '8px',
                background: location.pathname === '/activity-log' ? '#fdf3e8' : 'transparent',
                transition: 'all 0.2s ease',
                letterSpacing: '-0.01em'
              }}
            >
              Activity Log
            </Link>
          </nav>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: '#faf6f1',
            padding: '6px 14px',
            borderRadius: '10px',
            border: '1px solid #ece0d1'
          }}>
            <span style={{ fontSize: '11px', color: '#a69889', fontWeight: '500' }}>
              Location
            </span>
            <select
              value={selectedLocation}
              onChange={handleLocationChange}
              style={{
                padding: '4px 8px',
                border: 'none',
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: '600',
                background: 'transparent',
                color: '#3d2b1f',
                outline: 'none',
                cursor: 'pointer',
                WebkitAppearance: 'none',
                MozAppearance: 'none'
              }}
            >
              {locations.map(loc => (
                <option key={loc.id} value={loc.id} style={{ color: '#3d2b1f', background: 'white' }}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            paddingLeft: '16px',
            borderLeft: '1px solid #ece0d1'
          }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#3d2b1f', display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'flex-end' }}>
                {user?.staffName || user?.name || user?.username}
                <span style={{
                  fontSize: '9px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px',
                  background: (isManager || isSuperAdmin) ? '#f0e6ff' : '#fdf3e8',
                  color: '#3d2b1f', textTransform: 'uppercase', letterSpacing: '0.5px',
                  border: `1px solid ${(isManager || isSuperAdmin) ? '#d8c4f0' : '#ece0d1'}`
                }}>{isSuperAdmin ? 'Super Admin' : isManager ? 'Manager' : 'POS'}</span>
              </div>
              <div style={{ fontSize: '11px', color: '#a69889', marginTop: '1px' }}>
                {user?.email}
              </div>
            </div>
            <button
              onClick={handleLogout}
              style={{
                padding: '7px 14px',
                background: '#faf6f1',
                color: '#7a6b5d',
                border: '1px solid #ece0d1',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                letterSpacing: '-0.01em'
              }}
              onMouseEnter={e => { e.target.style.background = '#c88346'; e.target.style.color = 'white'; e.target.style.borderColor = '#c88346'; }}
              onMouseLeave={e => { e.target.style.background = '#faf6f1'; e.target.style.color = '#7a6b5d'; e.target.style.borderColor = '#ece0d1'; }}
            >
              Logout
            </button>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('toggle-activity-log'))}
              title="Activity Log"
              style={{
                padding: '7px 10px',
                background: '#faf6f1',
                color: '#7a6b5d',
                border: '1px solid #ece0d1',
                borderRadius: '8px',
                fontSize: '15px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                lineHeight: 1
              }}
              onMouseEnter={e => { e.target.style.background = '#fdf3e8'; e.target.style.borderColor = '#c88346'; }}
              onMouseLeave={e => { e.target.style.background = '#faf6f1'; e.target.style.borderColor = '#ece0d1'; }}
            >
              📋
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;